// ===================================
// ADMIN PRICING PAGE
// src/pages/AdminPricing.js
// Stage 11: Complete pricing management
// ===================================

import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './AdminPages.css';

const AdminPricing = () => {
  const [pricing, setPricing] = useState({
    text: { 15: 9.99, 30: 14.99, 60: 24.99 },
    voice: { 15: 14.99, 30: 24.99, 60: 39.99 },
    phone: { 15: 19.99, 30: 34.99, 60: 49.99 }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      setLoading(true);
      const response = await api.get('/config/pricing');
      if (response.data) {
        setPricing(prev => ({
          text: { ...prev.text, ...response.data.text },
          voice: { ...prev.voice, ...response.data.voice },
          phone: { ...prev.phone, ...response.data.phone }
        }));
      }
    } catch (err) {
      console.error('Error loading pricing:', err);
      // Keep default pricing if load fails
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (type, duration, value) => {
    const numValue = parseFloat(value) || 0;
    setPricing(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [duration]: numValue
      }
    }));
    setHasChanges(true);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Save pricing as a single setting
      await api.put('/admin/settings/pricing', { 
        value: JSON.stringify(pricing) 
      });

      // Also save individual settings for backward compatibility
      for (const [type, durations] of Object.entries(pricing)) {
        for (const [duration, price] of Object.entries(durations)) {
          await api.put(`/admin/settings/pricing_${duration}_${type}`, { 
            value: String(price) 
          });
        }
      }

      setSuccess('Pricing saved successfully!');
      setHasChanges(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving pricing:', err);
      setError('Failed to save pricing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const interviewTypes = [
    { 
      key: 'text', 
      name: 'Text Chat', 
      icon: '💬', 
      description: 'AI-powered text-based interview practice',
      color: '#3b82f6'
    },
    { 
      key: 'voice', 
      name: 'Web Voice', 
      icon: '🎙️', 
      description: 'Browser-based voice interview with AI',
      color: '#8b5cf6'
    },
    { 
      key: 'phone', 
      name: 'Phone Call', 
      icon: '📞', 
      description: 'Phone-based interview via VAPI',
      color: '#10b981'
    }
  ];

  const durations = [
    { minutes: 15, label: '15 min', description: 'Quick practice' },
    { minutes: 30, label: '30 min', description: 'Standard session' },
    { minutes: 60, label: '60 min', description: 'Deep dive' }
  ];

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading pricing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div className="header-content">
          <h1>💰 Pricing Management</h1>
          <p>Configure interview session pricing</p>
        </div>
        <button 
          onClick={handleSave} 
          className={`btn btn-primary ${!hasChanges ? 'btn-disabled' : ''}`}
          disabled={!hasChanges || saving}
        >
          {saving ? '💾 Saving...' : '💾 Save All Prices'}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>⚠️</span> {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>✅</span> {success}
        </div>
      )}

      {/* Pricing Cards */}
      <div className="pricing-management-grid">
        {interviewTypes.map(type => (
          <div key={type.key} className="pricing-card">
            <div className="pricing-card-header" style={{ borderColor: type.color }}>
              <span className="pricing-icon">{type.icon}</span>
              <div className="pricing-title">
                <h3>{type.name}</h3>
                <p>{type.description}</p>
              </div>
            </div>
            
            <div className="pricing-tiers">
              {durations.map(duration => (
                <div key={duration.minutes} className="pricing-tier">
                  <div className="tier-info">
                    <span className="tier-duration">{duration.label}</span>
                    <span className="tier-description">{duration.description}</span>
                  </div>
                  <div className="tier-price">
                    <span className="currency">$</span>
                    <input
                      type="number"
                      value={pricing[type.key][duration.minutes]}
                      onChange={(e) => handlePriceChange(type.key, duration.minutes, e.target.value)}
                      step="0.01"
                      min="0"
                      className="price-input"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="pricing-card-footer">
              <span className="pricing-note">
                💡 Prices are in USD
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Pricing Preview */}
      <div className="pricing-preview-section">
        <h3>📊 Pricing Preview</h3>
        <p>How prices will appear to customers</p>
        
        <div className="pricing-preview-grid">
          {durations.map(duration => (
            <div key={duration.minutes} className="preview-column">
              <div className="preview-header">
                <span className="preview-duration">{duration.label}</span>
              </div>
              {interviewTypes.map(type => (
                <div key={type.key} className="preview-row">
                  <span className="preview-icon">{type.icon}</span>
                  <span className="preview-type">{type.name}</span>
                  <span className="preview-price">
                    ${pricing[type.key][duration.minutes].toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Pricing Tips */}
      <div className="pricing-tips">
        <h3>💡 Pricing Tips</h3>
        <div className="tips-grid">
          <div className="tip-card">
            <span className="tip-icon">📈</span>
            <h4>Value-Based Pricing</h4>
            <p>Phone interviews should be priced higher due to real-time AI voice processing costs.</p>
          </div>
          <div className="tip-card">
            <span className="tip-icon">⏱️</span>
            <h4>Duration Discounts</h4>
            <p>Consider offering per-minute discounts for longer sessions to encourage deeper practice.</p>
          </div>
          <div className="tip-card">
            <span className="tip-icon">🎯</span>
            <h4>Competitive Analysis</h4>
            <p>Research similar platforms to ensure your pricing is competitive yet profitable.</p>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="unsaved-banner">
          <span>⚠️ You have unsaved changes</span>
          <div className="banner-actions">
            <button onClick={loadPricing} className="btn btn-ghost btn-sm">
              Discard
            </button>
            <button onClick={handleSave} className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPricing;